import React from "react";
import { motion } from "framer-motion";
import { DollarSign, ShieldCheck } from "lucide-react";
import { formatCurrency } from "./utils";

interface FinancialAlertsWidgetProps {
  alerts: any[];
}

const getFinancialAlertColor = (severity: string): string => {
  switch (severity) {
    case "high":
      return "border-l-red-500 bg-red-50";
    case "medium":
      return "border-l-orange-500 bg-orange-50";
    case "low":
      return "border-l-yellow-500 bg-yellow-50";
    default:
      return "border-l-gray-500 bg-gray-50";
  }
};

const getFinancialAlertText = (type: string): string => {
  switch (type) {
    case "trust_deficit":
      return "Trust balance in deficit";
    case "retainer_exhausted":
      return "Retainer exhausted";
    case "invoice_overdue":
      return "Invoice overdue";
    case "payment_missed":
      return "Payment plan missed";
    default:
      return "Financial alert";
  }
};

const FinancialAlertsWidget: React.FC<FinancialAlertsWidgetProps> = ({
  alerts,
}) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <DollarSign
          className="h-5 w-5 text-slate-700"
          aria-hidden="true"
        />
        <h2 className="text-lg font-bold" style={{ color: "#1F2937" }}>
          Financial Alerts
        </h2>
      </div>

      {!alerts || alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div
            className="h-12 w-12 rounded-full flex items-center justify-center mb-3"
            style={{ backgroundColor: "var(--color-primary-light)" }}
          >
            <ShieldCheck
              className="h-6 w-6"
              style={{ color: "var(--color-accent)" }}
            />
          </div>
          <p className="font-medium text-slate-500 text-sm">All clear</p>
          <p className="text-xs text-slate-400">
            No financial alerts at this time
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert: any, idx: number) => (
            <div
              key={alert._id || idx}
              className={`p-3 border-l-4 rounded-lg ${getFinancialAlertColor(
                alert.severity
              )}`}
            >
              <p
                className="font-medium text-sm mb-1"
                style={{ color: "#1F2937" }}
              >
                {alert.matterId?.title || ""}
              </p>
              <p className="text-xs text-slate-700 mb-1">
                {getFinancialAlertText(alert.type)}
              </p>
              {alert.amountCents != null && (
                <p
                  className="text-sm font-bold"
                  style={{ color: "#1F2937" }}
                >
                  {formatCurrency(alert.amountCents)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </motion.section>
  );
};

export default FinancialAlertsWidget;
